import React, { useRef, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { motion } from "framer-motion";

import osHeroImage from "../../../assets/images/os/os_heroimage.webp";
import osAudioJp from "../../../assets/audio/os/soremo_os_jp.mp3";

const HeroImageContainer = styled(motion.section).attrs({
  className: "u-w100 u-col-center",
})`
  position: relative;
  height: 100dvh;

  background-image: url(${osHeroImage}); /* 画像のパスを指定 */
  background-size: cover; /* 高さを要素の100%に設定し、幅は自動調整 */
  background-position: center center; /* 画像を中央に配置 */
  background-repeat: no-repeat; /* 画像の繰り返し無し */

  overflow: hidden;
  
  /* スクロールスナップの設定 */
  scroll-snap-align: start;
`;

const HeroTagline = styled.div.attrs({
  className: "u-text-justify",
})`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  text-align: center;
  padding: 0 20px;

  font-family: "din-2014-rounded-variable", sans-serif;
  font-variation-settings: "wght" 200;

  font-style: normal;

  font-size: clamp(4rem, 3.273rem + 3.64vw, 8rem);
  line-height: 100%;

  color: #000;
`;

const OsHeroImage = ({ isStarted, onStart, isAudioEnabled, onAudioToggle, hasActiveVideo, isInOpeningArea }) => {
  const isMobile = useSelector((state) => state.device.isMobile);
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // コンポーネントがマウントされたら自動的にページを開始状態にする
  useEffect(() => {
    const timer = setTimeout(() => {
      onStart();
    }, 1000); // 1秒後に自動開始

    return () => clearTimeout(timer);
  }, [onStart]);

  // オーディオの再生/停止を制御
  useEffect(() => {
    if (isAudioEnabled && !isPlaying) {
      handleFadeInAudio();
    } else if (!isAudioEnabled && isPlaying) {
      handleFadeOutAudio();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAudioEnabled]);

  // フェードインの処理
  const handleFadeInAudio = () => {
    if (!audioRef.current) return;
    let fadeVolume = 0.0;
    audioRef.current.volume = fadeVolume;
    audioRef.current.play();
    setIsPlaying(true);

    const fadeInterval = setInterval(() => {
      fadeVolume = fadeVolume + 0.1;
      if (fadeVolume >= 1.0) {
        audioRef.current.volume = 1.0;
        clearInterval(fadeInterval);
      } else {
        audioRef.current.volume = fadeVolume;
      }
    }, 200);
  };

  // フェードアウトの処理
  const handleFadeOutAudio = () => {
    if (!audioRef.current) return;

    let fadeVolume = audioRef.current.volume;
    const fadeInterval = setInterval(() => {
      // 0.1ずつボリュームを下げる
      fadeVolume = fadeVolume - 0.1;
      if (fadeVolume <= 0.0) {
        audioRef.current.volume = 0.0;
        audioRef.current.pause(); // 音量が0になったら停止
        clearInterval(fadeInterval);
        setIsPlaying(false);
      } else {
        audioRef.current.volume = fadeVolume;
      }
    }, 200); // 0.2秒ごとに処理を行う（調整可能）
  };

  // BGM制御ロジック
  useEffect(() => {
    // オープニングエリア内かつ動画が再生されていない場合のみBGMを再生
    if (isInOpeningArea && isAudioEnabled && !hasActiveVideo && !isPlaying) {
      handleFadeInAudio();
    } else if ((!isInOpeningArea || hasActiveVideo || !isAudioEnabled) && isPlaying) {
      handleFadeOutAudio();
    }
  }, [isInOpeningArea, isAudioEnabled, hasActiveVideo, isPlaying]);
  
  // オーディオ状態が変更されたときにBGMのmuted属性を更新
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = !isAudioEnabled;
    }
  }, [isAudioEnabled]);

  return (
    <>
      <HeroImageContainer
        initial={{ opacity: 0, scale: 2 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 2 }}
      >
        <HeroTagline>NEW PRODUCER OS</HeroTagline>
        <audio ref={audioRef} src={osAudioJp} loop />
      </HeroImageContainer>
    </>
  );
};

export default OsHeroImage;
